import api from '@/api';
import type { components } from '@/api/qraft/schema';

type ProfileType = components['schemas']['ProfileType'];

const STUDENT_TYPES: ProfileType[] = [
  'WEB_STUDENT',
  'LITEAPP_STUDENT',
  'EMAIL_STUDENT',
  'POSTAL_STUDENT',
];

/**
 * "Not a student" rather than "is a study helper": ADMIN, FOLLOWUP_COORDINATOR
 * and FOLLOWUP_PARTNER count along with STUDY_HELPER, so `isStudyHelper` only
 * tests the shell shape. The member layout's sidebar uses it for exactly that.
 *
 * Never use it to gate a relation between the viewer and another member:
 * `isStudyHelper && !isOwnProfile` showed the "viewing your student" notice to
 * far more viewers than assigned helpers. Use `useMemberViewerRelation` or
 * `me.profile_type` for anything narrower than the shell.
 *
 * TODO: Implement unique UI for more user types, rather than defining as only
 * student or study helper
 */
const STUDY_HELPER_TYPES: ProfileType[] = [
  'STUDY_HELPER',
  'FOLLOWUP_COORDINATOR',
  'FOLLOWUP_PARTNER',
  'ADMIN',
];

export function useCurrentUser() {
  const { data: me, isPending, ...rest } = api.getCurrentUser.useQuery();

  const isStudent = me?.profile_type
    ? STUDENT_TYPES.includes(me.profile_type)
    : false;

  const isStudyHelper = me?.profile_type
    ? STUDY_HELPER_TYPES.includes(me.profile_type)
    : false;

  const isAdmin = me?.profile_type === 'ADMIN';

  const isViewingOwnProfile = (memberId: string) =>
    me?.id?.toString() === memberId;

  return {
    me,
    isStudent,
    // Both role flags read false until `me` arrives, which looks like "not a
    // study helper". Anything that branches on the role must wait for
    // `isPending`, or its first render commits to the student answer and redoes
    // the work (and caches the 404) once the profile lands.
    isPending,
    isStudyHelper,
    isAdmin,
    isViewingOwnProfile,
    ...rest,
  };
}
